import React, { useState, useEffect } from 'react';
import {
  X,
  Check,
  Eye,
  Sliders,
  BarChart3,
  PieChart as PieIcon,
  TrendingUp,
  Table as TableIcon,
  Activity,
  Gauge,
  Palette,
  AlertTriangle,
  Database,
  Layers,
  Sparkles,
  Calculator,
} from 'lucide-react';
import {
  CustomAnalysisWidget,
  CreateWidgetDTO,
  UpdateWidgetDTO,
  AnalysisChartType,
  AnalysisDataSource,
  AnalysisAggregation,
  DataSourceCatalogItem,
  PALETTE_COLORS,
} from '../types';
import { analysisService } from '../analysisService';
import { AnalysisWidgetCard } from './AnalysisWidgetCard';
import { useEscapeKey } from '../../../shared/useGlobalModalEscape';

interface AnalysisWidgetBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (widgetData: CreateWidgetDTO | UpdateWidgetDTO) => void;
  catalog: DataSourceCatalogItem[];
  dashboardId: string;
  initialWidget?: CustomAnalysisWidget | null;
}

const DEFAULT_FALLBACK_CATALOG: DataSourceCatalogItem[] = [
  {
    id: 'asir_occurrences',
    name: 'ASIRs & MOR Safety Reporting Trends',
    description: 'ASIR safety reports, MOR mandatory occurrences, 10k FH/FC ratios, Category Level 4, aircraft registrations, routes, and closure duration',
    dimensions: [
      { field: 'date_year', label: 'Date / Year', type: 'dimension' },
      { field: 'date_quarter', label: 'Quarter (Qtr 1 - Qtr 4)', type: 'dimension' },
      { field: 'date_month', label: 'Month (Jan, Feb, ...)', type: 'dimension' },
      { field: 'category_level_4', label: 'Category Level 4 (Unruly Passenger, Bird Strike, etc.)', type: 'dimension' },
      { field: 'aircraft_reg', label: 'Aircraft Registration (TC-TJO, TC-TJU, etc.)', type: 'dimension' },
      { field: 'report_type', label: 'Report Type (Flight Ops, Cabin, Ground/Cargo, Fatigue, etc.)', type: 'dimension' },
      { field: 'route', label: 'Route (Flight Route: AMS/EHAM-AYT/LTAI, etc.)', type: 'dimension' },
      { field: 'department', label: 'Department (Technical, Training, Ground Ops, etc.)', type: 'dimension' },
      { field: 'investigator', label: 'Investigator', type: 'dimension' },
      { field: 'due_status', label: 'Due / Overdue Status (On-Time / Overdue)', type: 'dimension' },
      { field: 'severity', label: 'Severity Level (Critical, Major, Moderate, Low)', type: 'dimension' },
    ],
    metrics: [
      { field: 'asir_count', label: 'Count of ASIRs', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
      { field: 'report_rate', label: 'Report Rate', type: 'metric', supportedAggregations: ['AVG', 'MEDIAN', 'MAX', 'MIN'], defaultAggregation: 'AVG', unit: 'Rate' },
      { field: 'report_receipt_rate', label: 'Report Receipt Rate', type: 'metric', supportedAggregations: ['AVG', 'MEDIAN', 'MAX', 'MIN'], defaultAggregation: 'AVG', unit: 'Rate' },
      { field: 'mor_count', label: 'Count of MORs', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
      { field: 'mor_rate_10k_fc', label: 'MOR Rate per 10k FC', type: 'metric', supportedAggregations: ['AVG', 'MEDIAN', 'MAX', 'MIN'], defaultAggregation: 'AVG', unit: 'per 10k FC' },
      { field: 'mor_rate_10k_fh', label: 'MOR Rate per 10k FH', type: 'metric', supportedAggregations: ['AVG', 'MEDIAN', 'MAX', 'MIN'], defaultAggregation: 'AVG', unit: 'per 10k FH' },
      { field: 'closing_days', label: 'Average Closing Days', type: 'metric', supportedAggregations: ['AVG', 'MEDIAN', 'MIN', 'MAX'], defaultAggregation: 'AVG', unit: 'Days' },
      { field: 'overdue_rate', label: 'Overdue Rate (%)', type: 'metric', supportedAggregations: ['AVG', 'MEDIAN', 'MAX', 'MIN'], defaultAggregation: 'AVG', unit: '%' },
      { field: 'completed_before_due', label: 'Completed Before Due', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
      { field: 'overdue_count', label: 'Overdue Reports Count', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
      { field: 'open_reports_count', label: 'Open Reports Count by Investigator', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
    ],
  },
  {
    id: 'quality_assurance',
    name: 'QA & ASIRS Quality Assurance Distribution',
    description: 'Quality assurance and ASIRS distribution over time, operational base locations, occurrence classes, and causal factors',
    dimensions: [
      { field: 'date_year', label: 'Date / Year', type: 'dimension' },
      { field: 'specific_location', label: 'Specific Location / Base (AYT Base, DEL Base, AMS Base, SAW Base)', type: 'dimension' },
      { field: 'occurrence_class_category', label: 'Occurrence Class and Category (Info, Occurrence, Incident, Hazard)', type: 'dimension' },
      { field: 'causal_factors', label: 'Causal Factors (Facilities, Ground/Cabin Crew, Weather, etc.)', type: 'dimension' },
      { field: 'report_type', label: 'Report Type (Flight Ops, Cabin, Ground Ops, Non-Safety)', type: 'dimension' },
      { field: 'report_system', label: 'Reporting System (ASIR vs QA)', type: 'dimension' },
    ],
    metrics: [
      { field: 'qa_count', label: 'Count of QA Reports', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
      { field: 'asir_count', label: 'Count of ASIRs', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
      { field: 'total_volume', label: 'Total Reporting Volume', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
    ],
  },
  {
    id: 'risk_management',
    name: 'Operational Risk Management Register',
    description: 'Risk lifecycle stages (In Planning, In Progress, In Review, Reviewed, Approved, Archived), raised year, department, and risk taxonomy',
    dimensions: [
      { field: 'status', label: 'Status (In Planning, In Progress, In Review, Reviewed, Approved, Archived)', type: 'dimension' },
      { field: 'risk_raised_year', label: 'Risk Raised Year', type: 'dimension' },
      { field: 'department', label: 'Responsible Department', type: 'dimension' },
      { field: 'risk_type', label: 'Risk Type (Operational, Security, Safety, Financial, Information Security)', type: 'dimension' },
    ],
    metrics: [
      { field: 'count', label: 'Count of Risks', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
      { field: 'percentage', label: 'Process Distribution (%)', type: 'metric', supportedAggregations: ['AVG', 'MEDIAN', 'MAX', 'MIN'], defaultAggregation: 'AVG', unit: '%' },
      { field: 'risk_score', label: 'Risk Matrix Score', type: 'metric', supportedAggregations: ['AVG', 'MEDIAN', 'MAX', 'MIN'], defaultAggregation: 'AVG' },
    ],
  },
  {
    id: 'audits_dashboard',
    name: 'Quality Audits & Compliance Dashboard',
    description: 'Targeted audit schedules and trends, internal vs external audit comparisons, and auditee department breakdown',
    dimensions: [
      { field: 'target_start_date', label: 'Target Start Date / Year', type: 'dimension' },
      { field: 'audit_type', label: 'Audit Type (Internal vs External)', type: 'dimension' },
      { field: 'audit_status', label: 'Audit Status (Closed, Closed Pending Review, Open)', type: 'dimension' },
      { field: 'auditee_department', label: 'Auditee Department (Ground Ops, Training, Engineering, Aviation Security, etc.)', type: 'dimension' },
    ],
    metrics: [
      { field: 'count', label: 'Count of Audits', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
      { field: 'completion_rate', label: 'Audit Completion Rate (%)', type: 'metric', supportedAggregations: ['AVG', 'MEDIAN', 'MIN', 'MAX'], defaultAggregation: 'AVG', unit: '%' },
    ],
  },
  {
    id: 'findings_dashboard',
    name: 'Audit Findings & Non-Conformities',
    description: 'Findings over time by issued date, finding types (ASIRS vs Audit), status, and root cause classifications',
    dimensions: [
      { field: 'issued_date', label: 'Issued Date / Year', type: 'dimension' },
      { field: 'finding_type', label: 'Finding Type (ASIRS, Audit, Non-Conformance)', type: 'dimension' },
      { field: 'status', label: 'Status (Open, In Progress, Closed, Overdue)', type: 'dimension' },
      { field: 'root_cause', label: 'Root Cause Classification', type: 'dimension' },
    ],
    metrics: [
      { field: 'count', label: 'Count of Findings', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
      { field: 'closed_overdue_pct', label: 'Closed Overdue Findings (%)', type: 'metric', supportedAggregations: ['AVG', 'MEDIAN', 'MAX', 'MIN'], defaultAggregation: 'AVG', unit: '%' },
      { field: 'days_to_close', label: 'Days to Close Finding', type: 'metric', supportedAggregations: ['AVG', 'MEDIAN', 'MIN', 'MAX'], defaultAggregation: 'AVG', unit: 'Days' },
    ],
  },
  {
    id: 'spi_kpi_monitoring',
    name: 'Safety Performance Indicators (SPI & KPI Monitoring)',
    description: 'Safety and regulatory SPI matrix, 10k FH/FC rates, monthly actual trends, and alert threshold limits',
    dimensions: [
      { field: 'title', label: 'Indicator Title (Unstable Approach, TCAS RA, etc.)', type: 'dimension' },
      { field: 'indicator_type', label: 'Type (Company SPI vs Regulatory SPI)', type: 'dimension' },
      { field: 'indicator_class', label: 'Indicator (Lagging vs Leading)', type: 'dimension' },
      { field: 'period', label: 'Period (Month / Year)', type: 'dimension' },
      { field: 'metric_unit', label: 'Metric Unit (10000 FH, 10000 FC, Average, Percentage, Count)', type: 'dimension' },
      { field: 'month', label: 'Month (January - December)', type: 'dimension' },
      { field: 'year', label: 'Year', type: 'dimension' },
    ],
    metrics: [
      { field: 'actual_value', label: 'Average Monthly Value / Rate', type: 'metric', supportedAggregations: ['AVG', 'MEDIAN', 'MAX', 'MIN'], defaultAggregation: 'AVG' },
      { field: 'target_value', label: 'Target Value', type: 'metric', supportedAggregations: ['AVG', 'MEDIAN', 'MIN', 'MAX'], defaultAggregation: 'AVG' },
      { field: 'alert_threshold_1', label: 'Alert Level 1 (Warning Threshold)', type: 'metric', supportedAggregations: ['AVG', 'MAX', 'MIN'], defaultAggregation: 'AVG' },
      { field: 'alert_threshold_2', label: 'Alert Level 2 (Critical Action Threshold)', type: 'metric', supportedAggregations: ['AVG', 'MAX', 'MIN'], defaultAggregation: 'AVG' },
    ],
  },
  {
    id: 'missing_spi_data',
    name: 'Missing SPI & Telemetry Data Monitoring',
    description: 'Missing monthly indicator submissions, responsible operational departments, and data provider groups',
    dimensions: [
      { field: 'related_dept', label: 'Responsible Department', type: 'dimension' },
      { field: 'data_provider_dept', label: 'Data Provider Department', type: 'dimension' },
      { field: 'indicator_type', label: 'Indicator Type (Company SPI, Regulatory SPI)', type: 'dimension' },
      { field: 'pi_year', label: 'Indicator Year', type: 'dimension' },
      { field: 'specific_focus', label: 'Specific Focus Area', type: 'dimension' },
    ],
    metrics: [
      { field: 'missing_count', label: 'Count of Missing Indicators', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
      { field: 'missing_rate', label: 'Missing Indicator Rate (%)', type: 'metric', supportedAggregations: ['AVG', 'MEDIAN', 'MAX', 'MIN'], defaultAggregation: 'AVG', unit: '%' },
    ],
  },
  {
    id: 'occurrences',
    name: 'Occurrences & Incidents (Platform Core)',
    description: 'Verified operational safety events, ICAO ADREP / ECCAIRS taxonomy',
    dimensions: [
      { field: 'category', label: 'Event Category', type: 'dimension' },
      { field: 'severity', label: 'Severity Level (Critical, Major, Moderate, Low)', type: 'dimension' },
      { field: 'status', label: 'Workflow Status (New, Under Review, Closed)', type: 'dimension' },
      { field: 'month', label: 'Event Month (YYYY-MM)', type: 'dimension' },
      { field: 'aircraft_reg', label: 'Aircraft Registration', type: 'dimension' },
      { field: 'location', label: 'Location / Aerodrome', type: 'dimension' },
      { field: 'flight_number', label: 'Flight Number', type: 'dimension' },
    ],
    metrics: [
      { field: 'count', label: 'Total Occurrences', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
      { field: 'mandatory_count', label: 'Mandatory Occurrences (72h MOR)', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
      { field: 'high_severity_count', label: 'High Severity Cases (Critical / Major)', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
    ],
  },
  {
    id: 'reports',
    name: 'Frontline Ingestion Reports (Platform Core)',
    description: 'Frontline flight crew, cabin, ground, and maintenance reports',
    dimensions: [
      { field: 'report_type', label: 'Report Type', type: 'dimension' },
      { field: 'report_category', label: 'Category (Safety vs Non-Safety)', type: 'dimension' },
      { field: 'initial_severity', label: 'Initial Severity Level', type: 'dimension' },
      { field: 'status', label: 'Report Status', type: 'dimension' },
      { field: 'department', label: 'Assigned Department', type: 'dimension' },
      { field: 'identity_mode', label: 'Identity Mode (Identified, Confidential, Anonymous)', type: 'dimension' },
      { field: 'month', label: 'Submission Month (YYYY-MM)', type: 'dimension' },
    ],
    metrics: [
      { field: 'count', label: 'Total Reports', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
      { field: 'confidential_count', label: 'Confidential / Anonymous Reports', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
      { field: 'eccairs_count', label: 'Mandatory 72h MOR Reports', type: 'metric', supportedAggregations: ['COUNT', 'SUM'], defaultAggregation: 'COUNT' },
    ],
  },
];

export const AnalysisWidgetBuilderModal: React.FC<AnalysisWidgetBuilderModalProps> = ({
  isOpen,
  onClose,
  onSave,
  catalog,
  dashboardId,
  initialWidget,
}) => {
  useEscapeKey(onClose, isOpen);

  const [activeTab, setActiveTab] = useState<'source' | 'chart' | 'thresholds' | 'style'>('source');

  // Form State
  const [title, setTitle] = useState<string>('');
  const [chartType, setChartType] = useState<AnalysisChartType>('bar');
  const [dataSource, setDataSource] = useState<AnalysisDataSource>('asir_occurrences');
  const [dimension, setDimension] = useState<string>('category_level_4');
  const [metric, setMetric] = useState<string>('asir_count');
  const [aggregation, setAggregation] = useState<AnalysisAggregation>('COUNT');
  const [widthCol, setWidthCol] = useState<number>(6);
  const [heightPx, setHeightPx] = useState<number>(360);
  const [colorPalette, setColorPalette] = useState<string>('aviation_blue');
  const [customColor, setCustomColor] = useState<string>('');
  const [fontSize, setFontSize] = useState<'sm' | 'md' | 'lg'>('md');
  const [showLegend, setShowLegend] = useState<boolean>(true);
  const [showGrid, setShowGrid] = useState<boolean>(true);
  const [showLabels, setShowLabels] = useState<boolean>(false);
  const [targetThreshold, setTargetThreshold] = useState<string>('');
  const [warningThreshold, setWarningThreshold] = useState<string>('');
  const [criticalThreshold, setCriticalThreshold] = useState<string>('');
  const [enableCrossFilter, setEnableCrossFilter] = useState<boolean>(true);

  // Initialize or reset form when modal opens
  useEffect(() => {
    if (initialWidget) {
      setTitle(initialWidget.title);
      setChartType(initialWidget.chartType);
      setDataSource(initialWidget.dataSource);
      setDimension(initialWidget.dimension);
      setMetric(initialWidget.metric);
      setAggregation(initialWidget.aggregation || 'AVG');
      setWidthCol(initialWidget.widthCol);
      setHeightPx(initialWidget.heightPx);
      setColorPalette(initialWidget.colorPalette);
      setCustomColor(initialWidget.customColor || '');
      setFontSize(initialWidget.fontSize);
      setShowLegend(initialWidget.showLegend);
      setShowGrid(initialWidget.showGrid);
      setShowLabels(initialWidget.showLabels);
      setTargetThreshold(initialWidget.targetThreshold !== undefined ? String(initialWidget.targetThreshold) : '');
      setWarningThreshold(initialWidget.warningThreshold !== undefined ? String(initialWidget.warningThreshold) : '');
      setCriticalThreshold(initialWidget.criticalThreshold !== undefined ? String(initialWidget.criticalThreshold) : '');
      setEnableCrossFilter(initialWidget.enableCrossFilter);
    } else {
      setTitle('ASIRs by Category Level 4');
      setChartType('horizontal_bar');
      setDataSource('asir_occurrences');
      setDimension('category_level_4');
      setMetric('asir_count');
      setAggregation('COUNT');
      setWidthCol(6);
      setHeightPx(360);
      setColorPalette('aviation_blue');
      setCustomColor('');
      setFontSize('md');
      setShowLegend(true);
      setShowGrid(true);
      setShowLabels(false);
      setTargetThreshold('5');
      setWarningThreshold('15');
      setCriticalThreshold('25');
      setEnableCrossFilter(true);
    }
  }, [initialWidget, isOpen]);

  const [localCatalog, setLocalCatalog] = useState<DataSourceCatalogItem[]>(
    catalog && catalog.length > 0 ? catalog : DEFAULT_FALLBACK_CATALOG
  );

  useEffect(() => {
    if (catalog && catalog.length > 0) {
      setLocalCatalog(catalog);
    } else {
      analysisService
        .getCatalog()
        .then((cat) => {
          if (cat && cat.length > 0) {
            setLocalCatalog(cat);
          }
        })
        .catch(() => {});
    }
  }, [catalog, isOpen]);

  const activeCatalog = localCatalog && localCatalog.length > 0 ? localCatalog : DEFAULT_FALLBACK_CATALOG;
  const selectedSourceMeta = activeCatalog.find((c) => c.id === dataSource) || activeCatalog[0];
  const selectedMetricMeta = selectedSourceMeta?.metrics.find((m) => m.field === metric) || selectedSourceMeta?.metrics[0];

  // Auto-update dimension, metric & default aggregation when dataSource changes
  const handleDataSourceChange = (newSource: AnalysisDataSource) => {
    setDataSource(newSource);
    const meta = activeCatalog.find((c) => c.id === newSource);
    if (meta) {
      if (meta.dimensions.length > 0) setDimension(meta.dimensions[0].field);
      if (meta.metrics.length > 0) {
        const firstMet = meta.metrics[0];
        setMetric(firstMet.field);
        if (firstMet.defaultAggregation) {
          setAggregation(firstMet.defaultAggregation);
        } else if (firstMet.supportedAggregations && firstMet.supportedAggregations.length > 0) {
          setAggregation(firstMet.supportedAggregations[0]);
        }
      }
    }
  };

  const handleMetricChange = (newMetric: string) => {
    setMetric(newMetric);
    const metricDef = selectedSourceMeta?.metrics.find((m) => m.field === newMetric);
    if (metricDef) {
      if (metricDef.defaultAggregation) {
        setAggregation(metricDef.defaultAggregation);
      } else if (metricDef.supportedAggregations && !metricDef.supportedAggregations.includes(aggregation)) {
        setAggregation(metricDef.supportedAggregations[0]);
      }
    }
  };

  // Ensure dimension and metric are valid for selectedSourceMeta
  useEffect(() => {
    if (selectedSourceMeta) {
      if (!selectedSourceMeta.dimensions.some((d) => d.field === dimension)) {
        if (selectedSourceMeta.dimensions.length > 0) {
          setDimension(selectedSourceMeta.dimensions[0].field);
        }
      }
      if (!selectedSourceMeta.metrics.some((m) => m.field === metric)) {
        if (selectedSourceMeta.metrics.length > 0) {
          setMetric(selectedSourceMeta.metrics[0].field);
        }
      }
    }
  }, [selectedSourceMeta, dimension, metric]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    const payload: CreateWidgetDTO = {
      title: title.trim(),
      chartType,
      dataSource,
      dimension,
      metric,
      aggregation,
      widthCol,
      heightPx,
      colorPalette,
      customColor: customColor ? customColor.trim() : undefined,
      fontSize,
      showLegend,
      showGrid,
      showLabels,
      targetThreshold: targetThreshold ? Number(targetThreshold) : undefined,
      warningThreshold: warningThreshold ? Number(warningThreshold) : undefined,
      criticalThreshold: criticalThreshold ? Number(criticalThreshold) : undefined,
      enableCrossFilter,
    };

    onSave(payload);
    onClose();
  };

  const chartOptions: Array<{ type: AnalysisChartType; label: string; icon: React.ReactNode; desc: string }> = [
    { type: 'bar', label: 'Vertical Bar', icon: <BarChart3 size={18} />, desc: 'Category and period comparisons' },
    { type: 'horizontal_bar', label: 'Horizontal Bar', icon: <BarChart3 size={18} style={{ transform: 'rotate(90deg)' }} />, desc: 'Category Level 4, aircraft registrations, routes, and departments' },
    { type: 'line', label: 'Line Trend', icon: <TrendingUp size={18} />, desc: 'Monthly/annual trends, 10k FH/FC, and MOR ratios' },
    { type: 'area', label: 'Area Chart', icon: <Activity size={18} />, desc: 'Cumulative safety and quality volume density' },
    { type: 'pie', label: 'Pie Chart', icon: <PieIcon size={18} />, desc: 'Proportional part-to-whole distribution' },
    { type: 'donut', label: 'Donut Chart', icon: <Layers size={18} />, desc: 'Risk status and severity breakdowns' },
    { type: 'radar', label: 'Radar Chart', icon: <Sliders size={18} />, desc: 'Multi-dimensional department audit coverage' },
    { type: 'kpi', label: 'KPI Metric Counter', icon: <Sparkles size={18} />, desc: 'Highlighted metric value, percentage, and alert state' },
    { type: 'gauge', label: 'Gauge Meter', icon: <Gauge size={18} />, desc: 'Target level and threshold deviation gauge' },
    { type: 'table', label: 'Data Summary Table', icon: <TableIcon size={18} />, desc: 'Detailed findings and audit record table' },
  ];

  return (
    <div
      className="modal-backdrop"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-card modal-xl"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
          width: '100%',
          maxWidth: '1120px',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid #cbd5e1',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                padding: '8px',
                backgroundColor: '#eff6ff',
                color: '#2563eb',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Sliders size={20} />
            </div>
            <div>
              <h2 style={{ fontSize: '17px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                {initialWidget ? 'Edit Analytics Widget' : 'Add Analytics Widget / Visual Chart'}
              </h2>
              <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0' }}>
                Configure data source, dimensions, metrics, and calculation method (Average, Median, Max, Min, Count, Sum) to generate dynamic analytics charts.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="modal-close-btn"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#64748b',
              padding: '6px',
              borderRadius: '6px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab Navigation Headers */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #e2e8f0',
            backgroundColor: '#ffffff',
            padding: '0 24px',
          }}
        >
          {[
            { id: 'source', label: '1. Data Source & Metric', icon: <Database size={14} /> },
            { id: 'chart', label: '2. Visual Chart Type', icon: <BarChart3 size={14} /> },
            { id: 'thresholds', label: '3. Target & Thresholds', icon: <AlertTriangle size={14} /> },
            { id: 'style', label: '4. Layout & Styling', icon: <Palette size={14} /> },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                padding: '12px 18px',
                fontSize: '12px',
                fontWeight: 600,
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                borderBottom: activeTab === tab.id ? '2px solid #2563eb' : '2px solid transparent',
                color: activeTab === tab.id ? '#2563eb' : '#64748b',
                transition: 'all 0.15s ease',
              }}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Modal Body: Form Fields & Live Preview Panel */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '7fr 5fr',
            gap: '24px',
            padding: '24px',
            overflowY: 'auto',
            maxHeight: '70vh',
          }}
        >
          {/* Left / Main Form Area (7 Cols) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Widget Title Input */}
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>
                Widget / Chart Title <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. ASIRs by Category Level 4"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '13px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  color: '#0f172a',
                  outline: 'none',
                }}
                required
              />
            </div>

            {/* TAB 1: Data Source, Dimension, Metric & Calculation Type */}
            {activeTab === 'source' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>
                    Data Source
                  </label>
                  <select
                    value={dataSource}
                    onChange={(e) => handleDataSourceChange(e.target.value as AnalysisDataSource)}
                    style={{
                      width: '100%',
                      padding: '9px 12px',
                      fontSize: '13px',
                      fontWeight: 600,
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      backgroundColor: '#ffffff',
                      color: '#0f172a',
                      outline: 'none',
                    }}
                  >
                    {activeCatalog.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  {selectedSourceMeta && (
                    <p style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', lineHeight: '1.4' }}>
                      {selectedSourceMeta.description}
                    </p>
                  )}
                </div>

                {/* 3-Column: Dimension (X), Metric (Y), Calculation / Aggregation */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                  {/* Dimension */}
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>
                      Dimension (X-Axis / Grouping)
                    </label>
                    <select
                      value={dimension}
                      onChange={(e) => setDimension(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        fontSize: '12px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        backgroundColor: '#ffffff',
                        color: '#0f172a',
                        outline: 'none',
                      }}
                    >
                      {selectedSourceMeta?.dimensions.map((dim) => (
                        <option key={dim.field} value={dim.field}>
                          {dim.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Metric */}
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>
                      Metric (Y-Axis / Value)
                    </label>
                    <select
                      value={metric}
                      onChange={(e) => handleMetricChange(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        fontSize: '12px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        backgroundColor: '#ffffff',
                        color: '#0f172a',
                        outline: 'none',
                      }}
                    >
                      {selectedSourceMeta?.metrics.map((m) => (
                        <option key={m.field} value={m.field}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Calculation / Aggregation */}
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', marginBottom: '4px' }}>
                      Calculation Method (Aggregation)
                    </label>
                    <select
                      value={aggregation}
                      onChange={(e) => setAggregation(e.target.value as AnalysisAggregation)}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        fontSize: '12px',
                        fontWeight: 600,
                        borderRadius: '8px',
                        border: '1px solid #bfdbfe',
                        backgroundColor: '#eff6ff',
                        color: '#1d4ed8',
                        outline: 'none',
                      }}
                    >
                      <option value="AVG">Average (Mean Value)</option>
                      <option value="MEDIAN">Median (Middle Value)</option>
                      <option value="MAX">Max (Maximum Value)</option>
                      <option value="MIN">Min (Minimum Value)</option>
                      <option value="COUNT">Count (Record Count)</option>
                      <option value="SUM">Sum (Cumulative Total)</option>
                    </select>
                  </div>
                </div>

                {/* Calculation Info & Recommendation Card */}
                <div
                  style={{
                    padding: '12px 14px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Calculator size={15} color="#2563eb" /> Metric & Mathematical Calculation Summary:
                    </span>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 700,
                          backgroundColor: '#eff6ff',
                          color: '#2563eb',
                          border: '1px solid #bfdbfe',
                        }}
                      >
                        Selected Calculation: {aggregation === 'AVG' ? 'Average' : aggregation === 'MEDIAN' ? 'Median' : aggregation === 'MAX' ? 'Max' : aggregation === 'MIN' ? 'Min' : aggregation === 'SUM' ? 'Sum' : 'Count'}
                      </span>
                      {selectedMetricMeta?.unit && (
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600,
                            backgroundColor: '#f1f5f9',
                            color: '#475569',
                          }}
                        >
                          Unit: {selectedMetricMeta.unit}
                        </span>
                      )}
                    </div>
                  </div>
                  <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>
                    This chart aggregates data from <strong>{selectedSourceMeta?.name}</strong> grouped by <strong>{dimension}</strong> calculating <strong>{metric}</strong> using <strong>{aggregation}</strong>.
                  </p>
                </div>

                {/* Cross Filtering Toggle */}
                <div
                  style={{
                    padding: '12px 14px',
                    backgroundColor: '#eff6ff',
                    borderRadius: '8px',
                    border: '1px solid #bfdbfe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e40af', display: 'block' }}>
                      Interactive Cross-Filtering
                    </span>
                    <span style={{ fontSize: '11px', color: '#1d4ed8' }}>
                      Clicking any bar, slice, or route on this chart dynamically cross-filters other widgets on the dashboard.
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    checked={enableCrossFilter}
                    onChange={(e) => setEnableCrossFilter(e.target.checked)}
                    style={{ width: '18px', height: '18px', accentColor: '#2563eb', cursor: 'pointer' }}
                  />
                </div>
              </div>
            )}

            {/* TAB 2: Chart Type */}
            {activeTab === 'chart' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: '2px' }}>
                  Select Visual Chart Type
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {chartOptions.map((opt) => (
                    <button
                      key={opt.type}
                      type="button"
                      onClick={() => setChartType(opt.type)}
                      style={{
                        padding: '10px 12px',
                        borderRadius: '8px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px',
                        transition: 'all 0.15s ease',
                        border: chartType === opt.type ? '2px solid #2563eb' : '1px solid #e2e8f0',
                        backgroundColor: chartType === opt.type ? '#eff6ff' : '#ffffff',
                      }}
                    >
                      <div
                        style={{
                          padding: '6px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: chartType === opt.type ? '#2563eb' : '#f1f5f9',
                          color: chartType === opt.type ? '#ffffff' : '#64748b',
                        }}
                      >
                        {opt.icon}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a' }}>{opt.label}</div>
                        <div style={{ fontSize: '11px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {opt.desc}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '6px', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#334155', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={showLegend}
                      onChange={(e) => setShowLegend(e.target.checked)}
                      style={{ accentColor: '#2563eb' }}
                    />
                    Show Legend
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#334155', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={showGrid}
                      onChange={(e) => setShowGrid(e.target.checked)}
                      style={{ accentColor: '#2563eb' }}
                    />
                    Grid Lines
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#334155', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={showLabels}
                      onChange={(e) => setShowLabels(e.target.checked)}
                      style={{ accentColor: '#2563eb' }}
                    />
                    Data Labels
                  </label>
                </div>
              </div>
            )}

            {/* TAB 3: Thresholds & Alert Levels */}
            {activeTab === 'thresholds' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                  Define reference averages (AVG), targets, or safety alert threshold limits.
                </p>

                <div style={{ padding: '12px', backgroundColor: '#ecfdf5', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#065f46', textTransform: 'uppercase', marginBottom: '4px' }}>
                    Target / Average Reference Line
                  </label>
                  <input
                    type="number"
                    value={targetThreshold}
                    onChange={(e) => setTargetThreshold(e.target.value)}
                    placeholder="e.g. 5 (or 23)"
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      fontSize: '13px',
                      borderRadius: '6px',
                      border: '1px solid #6ee7b7',
                      backgroundColor: '#ffffff',
                      outline: 'none',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#047857', display: 'block', marginTop: '4px' }}>
                    Rendered as a green dashed reference line on the chart (e.g. AVG: 5 or AVG: 23 days).
                  </span>
                </div>

                <div style={{ padding: '12px', backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', marginBottom: '4px' }}>
                    Warning Threshold (Yellow Reference Line)
                  </label>
                  <input
                    type="number"
                    value={warningThreshold}
                    onChange={(e) => setWarningThreshold(e.target.value)}
                    placeholder="e.g. 15"
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      fontSize: '13px',
                      borderRadius: '6px',
                      border: '1px solid #fcd34d',
                      backgroundColor: '#ffffff',
                      outline: 'none',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#b45309', display: 'block', marginTop: '4px' }}>
                    Triggers a yellow warning threshold line and indicator badge when values reach this level.
                  </span>
                </div>

                <div style={{ padding: '12px', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', marginBottom: '4px' }}>
                    Critical Action Line (Red Reference Line)
                  </label>
                  <input
                    type="number"
                    value={criticalThreshold}
                    onChange={(e) => setCriticalThreshold(e.target.value)}
                    placeholder="e.g. 25"
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      fontSize: '13px',
                      borderRadius: '6px',
                      border: '1px solid #fca5a5',
                      backgroundColor: '#ffffff',
                      outline: 'none',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#b91c1c', display: 'block', marginTop: '4px' }}>
                    Indicates high safety priority requiring corrective actions and displays a red limit line.
                  </span>
                </div>
              </div>
            )}

            {/* TAB 4: Layout & Appearance */}
            {activeTab === 'style' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>
                      Card Width (12-Column Grid)
                    </label>
                    <select
                      value={widthCol}
                      onChange={(e) => setWidthCol(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        fontSize: '13px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        backgroundColor: '#ffffff',
                        color: '#0f172a',
                        outline: 'none',
                      }}
                    >
                      <option value={3}>1/4 Width (3 Columns)</option>
                      <option value={4}>1/3 Width (4 Columns)</option>
                      <option value={6}>1/2 Width (6 Columns - Half Row)</option>
                      <option value={8}>2/3 Width (8 Columns)</option>
                      <option value={12}>Full Width (12 Columns - Full Row)</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>
                      Card Height (Pixels)
                    </label>
                    <select
                      value={heightPx}
                      onChange={(e) => setHeightPx(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        fontSize: '13px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        backgroundColor: '#ffffff',
                        color: '#0f172a',
                        outline: 'none',
                      }}
                    >
                      <option value={280}>Compact (280px)</option>
                      <option value={360}>Standard (360px)</option>
                      <option value={440}>Expanded (440px)</option>
                      <option value={520}>Large (520px)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: '6px' }}>
                    Aviation Color Palette
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {Object.entries(PALETTE_COLORS).map(([palKey, colors]) => (
                      <button
                        key={palKey}
                        type="button"
                        onClick={() => setColorPalette(palKey)}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: colorPalette === palKey ? '2px solid #2563eb' : '1px solid #cbd5e1',
                          backgroundColor: colorPalette === palKey ? '#eff6ff' : '#ffffff',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                          textAlign: 'left',
                        }}
                      >
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#1e293b', textTransform: 'capitalize' }}>
                          {palKey.replace('_', ' ')}
                        </span>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {colors.slice(0, 5).map((c, i) => (
                            <div key={i} style={{ width: '14px', height: '14px', borderRadius: '3px', backgroundColor: c }} />
                          ))}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right / Live Preview Area (5 Cols) */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '16px',
              backgroundColor: '#f8fafc',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingBottom: '10px',
                borderBottom: '1px solid #e2e8f0',
                marginBottom: '12px',
              }}
            >
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#475569',
                  textTransform: 'uppercase',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Eye size={15} color="#2563eb" /> Live Chart Preview
              </span>
              <span style={{ fontSize: '11px', color: '#94a3b8' }}>Dynamic Database Query</span>
            </div>

            <div style={{ flex: 1, minHeight: '320px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0', padding: '6px', overflow: 'hidden' }}>
                <AnalysisWidgetCard
                  widget={{
                    id: 'PREVIEW_TEMP',
                    dashboardId,
                    organizationId: 'ORG-XYZ',
                    title: title || 'Widget Preview',
                    chartType,
                    dataSource,
                    dimension,
                    metric,
                    aggregation,
                    widthCol: 12,
                    heightPx: 300,
                    sortOrder: 1,
                    colorPalette,
                    customColor: customColor || undefined,
                    fontSize,
                    showLegend,
                    showGrid,
                    showLabels,
                    targetThreshold: targetThreshold ? Number(targetThreshold) : undefined,
                    warningThreshold: warningThreshold ? Number(warningThreshold) : undefined,
                    criticalThreshold: criticalThreshold ? Number(criticalThreshold) : undefined,
                    enableCrossFilter,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  }}
                  filters={{}}
                  isEditMode={false}
                  onEditWidget={() => {}}
                  onDeleteWidget={() => {}}
                  onCrossFilter={() => {}}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 24px',
            borderTop: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc',
          }}
        >
          <div style={{ display: 'flex', gap: '8px' }}>
            {activeTab !== 'source' && (
              <button
                type="button"
                onClick={() => {
                  if (activeTab === 'style') setActiveTab('thresholds');
                  else if (activeTab === 'thresholds') setActiveTab('chart');
                  else if (activeTab === 'chart') setActiveTab('source');
                }}
                className="btn-secondary"
                style={{ width: 'auto', padding: '7px 14px', fontSize: '12px' }}
              >
                Back
              </button>
            )}
            {activeTab !== 'style' && (
              <button
                type="button"
                onClick={() => {
                  if (activeTab === 'source') setActiveTab('chart');
                  else if (activeTab === 'chart') setActiveTab('thresholds');
                  else if (activeTab === 'thresholds') setActiveTab('style');
                }}
                className="btn-secondary"
                style={{ width: 'auto', padding: '7px 14px', fontSize: '12px', color: '#2563eb', borderColor: '#bfdbfe' }}
              >
                Next: {activeTab === 'source' ? 'Chart Type' : activeTab === 'chart' ? 'Thresholds' : 'Layout & Style'}
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              style={{ width: 'auto', padding: '7px 14px', fontSize: '12px' }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="btn-primary"
              style={{ width: 'auto', padding: '7px 18px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <Check size={15} /> {initialWidget ? 'Save Changes' : 'Add Widget to Dashboard'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
